/**
 * Centralized timezone helpers — Nexflow Hub operates on America/New_York (Ohio).
 *
 * Why this exists:
 * - Shift date/start_time/end_time are stored as TZ-naive strings (e.g. "2026-07-07", "09:00").
 *   Those render the same regardless of viewer location — no change needed.
 * - ISO timestamps (created_at, claimed_at, etc.) ARE timezone-aware. Without explicit
 *   formatting they render in the VIEWER'S browser TZ, which is wrong when a customer
 *   or staff member accesses from another timezone.
 * - "Today / this week / next week" calendar computations naturally use the browser TZ;
 *   for a viewer outside ET this can shift the highlighted day by one.
 *
 * All Intl calls use the IANA identifier 'America/New_York' so DST (spring forward /
 * fall back) is handled automatically — no hardcoded offsets.
 */
export const ET_TIMEZONE = 'America/New_York';

const _fmt = (opts) => new Intl.DateTimeFormat('en-US', { timeZone: ET_TIMEZONE, ...opts });

/** Full date + time, e.g. "Jul 7, 2026, 9:05 AM" — for activity logs / comm history. */
export const fmtETDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return _fmt({ year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
  } catch {
    return String(iso);
  }
};

/** Compact date, e.g. "Jul 7, 2026". */
export const fmtETDate = (iso) => {
  if (!iso) return '—';
  try {
    return _fmt({ year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(iso));
  } catch {
    return String(iso);
  }
};

/** Compact time, e.g. "9:05 AM". */
export const fmtETTime = (iso) => {
  if (!iso) return '—';
  try {
    return _fmt({ hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
  } catch {
    return String(iso);
  }
};

/** Weekday short label, e.g. "Mon". */
export const fmtETWeekday = (d) => {
  if (!d) return '';
  try { return _fmt({ weekday: 'short' }).format(new Date(d)); } catch { return ''; }
};

/** "Jul 7" for week headers. */
export const fmtETMonthDay = (d) => {
  if (!d) return '';
  try { return _fmt({ month: 'short', day: 'numeric' }).format(new Date(d)); } catch { return ''; }
};

/** "7/7" numeric short for tight day cells. */
export const fmtETNumeric = (d) => {
  if (!d) return '';
  try { return _fmt({ month: 'numeric', day: 'numeric' }).format(new Date(d)); } catch { return ''; }
};

/** Returns the current wall-clock ISO date string in America/New_York, e.g. "2026-07-07".
 *  Use this instead of `new Date().toISOString().slice(0,10)` (which is UTC) or
 *  `toISODate(new Date())` (which depends on viewer TZ). */
export const todayISO_ET = () => isoDateET(new Date());

/** Convert a Date (any TZ) to the ISO date string ("YYYY-MM-DD") of its America/New_York calendar date. */
export const isoDateET = (d) => {
  if (!d) return '';
  const parts = _fmt({ year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(d));
  const y = parts.find((p) => p.type === 'year').value;
  const m = parts.find((p) => p.type === 'month').value;
  const day = parts.find((p) => p.type === 'day').value;
  return `${y}-${m}-${day}`;
};

/** Returns the Sunday-start of the week containing `d`, computed by America/New_York calendar
 *  (so a 11pm-Pacific click doesn't roll the calendar a day forward).
 *  Returns a Date pinned to ET midnight of that Sunday. */
export const weekStartET = (d = new Date()) => {
  // Find the ET weekday (0=Sun … 6=Sat) of d by formatting it in ET.
  const weekday = _fmt({ weekday: 'short' }).format(new Date(d));
  const dayIdx = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].indexOf(weekday);
  const etIsoDate = isoDateET(new Date(d));
  // Build the ET-anchored noon Date for that day; we use noon to avoid any DST edge
  // collisions on the boundary itself.
  const base = new Date(`${etIsoDate}T12:00:00-05:00`); // offset is irrelevant; we re-anchor by ET below
  base.setUTCDate(base.getUTCDate() - dayIdx);
  return base;
};

/** iter149 — Returns the Saturday-start of the week containing `d`, computed by
 *  America/New_York calendar. This matches the Nexflow Plumbing payroll/business
 *  week (Saturday → Friday) and is used by /admin/schedule + AdminTimesheets so
 *  the schedule grid and payroll grid line up.
 *
 *  Saturday → 0 days back, Sunday → 1 day back, …, Friday → 6 days back. */
export const weekStartSaturdayET = (d = new Date()) => {
  const weekday = _fmt({ weekday: 'short' }).format(new Date(d));
  const dayIdx = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].indexOf(weekday);
  // Days back from Saturday: Sat=0, Sun=1, Mon=2, Tue=3, Wed=4, Thu=5, Fri=6.
  const back = (dayIdx + 1) % 7;
  const etIsoDate = isoDateET(new Date(d));
  const base = new Date(`${etIsoDate}T12:00:00-05:00`);
  base.setUTCDate(base.getUTCDate() - back);
  return base;
};

/** Add N days to a Date and return a new Date (TZ-safe — does not jump across DST gaps). */
export const addDaysET = (d, n) => {
  const r = new Date(d);
  r.setUTCDate(r.getUTCDate() + n);
  return r;
};
